import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  SxProps,
  TextField,
  useTheme
} from '@mui/material'
import { SelectOption } from '@/core/types/SelectOption.ts'
import { Fragment, ReactNode } from 'react'
import { Controller } from 'react-hook-form'

interface Props {
  options: SelectOption<object, string | number>[]
  label?: string
  fullWidth?: boolean
  helperText?: ReactNode
  error?: boolean
  loading?: boolean
  onChange?: (e: { target: { value: string } }) => void
  name: string
  control?: any
  placeholder?: string
  sx?: SxProps
  renderOption?: AutocompleteProps<any, any, any, any>['renderOption']
}

/**
 * This component is intended to be used in a controlled way.
 * ie: in forms and places where you want to control and set
 * its value from outside.
 */

const SelectComponentControlled = ({
  options,
  label,
  fullWidth,
  helperText,
  error,
  loading,
  name,
  control,
  placeholder,
  sx,
  renderOption,
  ...props
}: Props & Omit<AutocompleteProps<any, any, any, any>, 'renderInput'>) => {
  const { extraColors } = useTheme()

  const autoCompleteUI = (
    value: any,
    onChange: any,
    ref: any,
    name?: string
  ) => (
    <Autocomplete
      disablePortal={false}
      options={options}
      loading={loading}
      value={value ? (options.find((o) => o.id === value) ?? null) : null}
      onChange={(_, val) => onChange(val ? val.id : null)}
      {...props}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={sx}
          label={label}
          fullWidth={fullWidth}
          helperText={helperText}
          error={error}
          inputRef={ref}
          name={name}
          placeholder={placeholder}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <CircularProgress
                      sx={{ color: extraColors.secondaryLoading }}
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              )
            }
          }}
        />
      )}
      size="small"
    />
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, ref } }) =>
        autoCompleteUI(value, onChange, ref)
      }
    />
  )
}
export default SelectComponentControlled
